/**
 * Quản lý đồng thuận (T90) — patient consent under Nghị định 13/2023/NĐ-CP.
 * Real /api/v1/consent. consent_records/withdrawals are the legal trail.
 */
import { apiClient } from "./client";

export interface ConsentRow {
  id: string;
  patientId: string;
  scope: string;
  capturedVia: string;
  grantedAt: string;
  grantedBy: string | null;
  withdrawn: boolean;
  withdrawnAt: string | null;
  withdrawReason: string | null;
  textCode: string | null;
  textVersion: string | null;
}

export interface ScopeOption {
  code: string;
  label: string;
  /**
   * consent_text_versions.code this scope is recorded against (#18618/#18912).
   * Sent by the server so the screen never has to guess which document to show —
   * showing the wrong text is the same false statement as recording the wrong one.
   */
  textCode: string | null;
}

export interface ConsentTextVersion {
  id: string;
  code: string;
  version: string;
  bodyVi: string;
  effectiveFrom: string;
}

export const CAPTURED_VIA: { value: string; label: string }[] = [
  { value: "IN_PERSON", label: "Trực tiếp tại quầy" },
  { value: "ELECTRONIC", label: "Điện tử (app/web)" },
  { value: "PHONE", label: "Qua điện thoại" },
  { value: "PAPER", label: "Bản giấy có chữ ký" },
];

export function capturedViaLabel(v: string): string {
  return CAPTURED_VIA.find((x) => x.value === v)?.label ?? v;
}

export const consentApi = {
  scopes(): Promise<ScopeOption[]> {
    return apiClient.get<ScopeOption[]>("/v1/consent/scopes").then((r) => r.data);
  },
  textVersions(): Promise<ConsentTextVersion[]> {
    return apiClient
      .get<ConsentTextVersion[]>("/v1/consent/text-versions")
      .then((r) => r.data);
  },
  list(patientId: string): Promise<ConsentRow[]> {
    return apiClient
      .get<ConsentRow[]>("/v1/consent", { params: { patientId } })
      .then((r) => r.data);
  },
  capture(
    patientId: string,
    scope: string,
    capturedVia: string
  ): Promise<{ id: string }> {
    return apiClient
      .post<{ id: string }>("/v1/consent", { patientId, scope, capturedVia })
      .then((r) => r.data);
  },
  withdraw(id: string, reason: string): Promise<void> {
    return apiClient
      .post(`/v1/consent/${id}/withdraw`, { reason })
      .then(() => undefined);
  },
};

export const consentQueryKeys = {
  scopes: () => ["consent", "scopes"] as const,
  textVersions: () => ["consent", "text-versions"] as const,
  list: (pid: string) => ["consent", "list", pid] as const,
};
